import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, LogOut, Command as CommandIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { ROLE_LABELS } from "@/lib/theme";
import { MobileMenu } from "@/components/layout/MobileNav";
import { NotificationCenter } from "@/components/NotificationCenter";

export function TopBar({ onOpenSearch, onNewLead }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canSettings = ["ADMIN", "SALES_MANAGER"].includes(user?.role);
  const isAccounts = user?.role === "ACCOUNTS";

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur md:px-6">
      <MobileMenu />

      <button data-testid="global-search-trigger" onClick={onOpenSearch}
        className="group flex h-9 flex-1 max-w-md items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-400 transition hover:border-slate-300 hover:bg-white">
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search leads, companies, contacts…</span>
        <kbd className="hidden items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 sm:flex">
          <CommandIcon className="h-2.5 w-2.5" />K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-2">
        {!isAccounts && (
          <>
            <Button data-testid="topbar-new-lead" onClick={onNewLead} size="sm" className="hidden sm:inline-flex gap-1.5 font-semibold">
              <Plus className="h-4 w-4" /> New Lead
            </Button>
            <button data-testid="topbar-new-lead-mobile" onClick={onNewLead}
              className="sm:hidden flex h-9 w-9 items-center justify-center rounded-lg text-white" style={{ backgroundColor: "#0057A8" }}>
              <Plus className="h-5 w-5" />
            </button>
          </>
        )}

        <NotificationCenter />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="topbar-avatar" className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: "#E31E24" }}>
              {initials(user?.name)}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold text-slate-800">{user?.name}</div>
              <div className="text-xs font-normal text-slate-500">{ROLE_LABELS[user?.role] || user?.role}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {canSettings && <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>Settings</DropdownMenuItem>}
            <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600 focus:text-red-600">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
